import { Capacitor } from '@capacitor/core';

/**
 * Single decision point for Android hardware / system back. App.vue's
 * `useBackButton` is the only executor. Ionic swipe-back stays off on Android
 * so a left-edge swipe and the hardware key share one pop (ONE-8286 — Ionic's
 * iOS-mode gesture plus a Capacitor `backButton` listener used to pop twice).
 */

export const TAB_ROOT_PATHS = ['/tabs/home', '/tabs/library', '/tabs/all', '/tabs/me'];

export const ROOT_EXIT_PATHS = [
  '/',
  '/home',
  '/pre-sign-in',
  '/login',
  '/register',
  '/success-launch',
];

export const HARDWARE_BACK = {
  IGNORE: 'ignore',
  EXIT: 'exit',
  CUSTOM: 'custom',
  POP: 'pop',
};

export function isTabRootPath(path) {
  return TAB_ROOT_PATHS.includes(path);
}

export function isRootExitPath(path) {
  return ROOT_EXIT_PATHS.includes(path);
}

/** Ionic iOS-mode swipe-back is a JS gesture; Android already has system back. */
export function isIonicSwipeBackEnabled(platform = Capacitor.getPlatform()) {
  return platform !== 'android';
}

/**
 * Decide what a single Android hardware / system-back event should do.
 *
 * @param {object} input
 * @param {boolean} input.disableBack - route meta `disableSwipeNavigation`
 * @param {boolean} input.isOnTabRoute - one of the four tab roots
 * @param {boolean} input.hasCustomHandler - navigationService has a page handler
 * @param {boolean} input.canGoBack - ion-router has a stacked view
 * @param {boolean} input.isRootRoute - unauthenticated / bootstrap root
 * @returns {'ignore'|'exit'|'custom'|'pop'}
 */
export function resolveHardwareBackAction({
  disableBack,
  isOnTabRoute,
  hasCustomHandler,
  canGoBack,
  isRootRoute,
}) {
  // Custom handlers win first so a multi-step form can consume back even when
  // the route also opted out of swipe-back.
  if (hasCustomHandler) return HARDWARE_BACK.CUSTOM;
  // `/tabs` sets disableSwipeNavigation so the tab roots do not swipe to the
  // previous tab. Hardware back on those roots still exits.
  if (isOnTabRoute) return HARDWARE_BACK.EXIT;
  if (disableBack) return HARDWARE_BACK.IGNORE;
  if (canGoBack) return HARDWARE_BACK.POP;
  if (isRootRoute) return HARDWARE_BACK.EXIT;
  return HARDWARE_BACK.IGNORE;
}
